import mapboxgl from 'mapbox-gl';

// Mapbox API configuration
export const getMapboxApiKey = (): string => {
  const apiKey = import.meta.env.VITE_MAPBOX_API_KEY as string;

  if (!apiKey) {
    console.error('Mapbox API key not found. Set VITE_MAPBOX_API_KEY env var.');
  }

  return apiKey || '';
};

// Initialize Mapbox with API key
export const initializeMapbox = () => {
  const apiKey = getMapboxApiKey();
  if (apiKey) {
    mapboxgl.accessToken = apiKey;
  }
};

// Geocoding service
export interface GeocodeResult {
  id: string;
  place_name: string;
  center: [number, number]; // [lng, lat]
  bbox?: [number, number, number, number];
  text: string;
}

export const geocodeAddress = async (query: string, proximity?: { lng: number; lat: number }): Promise<GeocodeResult[]> => {
  const apiKey = getMapboxApiKey();
  if (!apiKey) {
    console.error('[mapboxService] No API key found');
    throw new Error('Mapbox API key not configured');
  }

  // Remove country restriction to support global locations
  const types = 'address,postcode,place,locality,neighborhood';

  const proximityParam = proximity ? `&proximity=${proximity.lng},${proximity.lat}` : '';
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
    `access_token=${apiKey}&types=${types}&limit=5${proximityParam}`;

  console.log('[mapboxService] Geocoding URL:', url.replace(apiKey, 'API_KEY_HIDDEN'));

  try {
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[mapboxService] Geocoding failed:', response.status, errorText);
      throw new Error(`Geocoding request failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('[mapboxService] Geocoding response:', data);
    return data.features || [];
  } catch (error) {
    console.error('[mapboxService] Geocoding error:', error);
    throw error;
  }
};

// Reverse geocoding — coordinates to place name
// Uses types valid for reverse geocoding (address type causes 422 errors with coordinates)
export const reverseGeocode = async (lng: number, lat: number): Promise<GeocodeResult[]> => {
  const apiKey = getMapboxApiKey();
  if (!apiKey) {
    console.error('[mapboxService] No API key found');
    throw new Error('Mapbox API key not configured');
  }

  const types = 'place,locality,region,country';
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?` +
    `access_token=${apiKey}&types=${types}&limit=1`;

  console.log('[mapboxService] Reverse geocoding URL:', url.replace(apiKey, 'API_KEY_HIDDEN'));

  try {
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[mapboxService] Reverse geocoding failed:', response.status, errorText);
      throw new Error(`Reverse geocoding request failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('[mapboxService] Reverse geocoding response:', data);
    return data.features || [];
  } catch (error) {
    console.error('[mapboxService] Reverse geocoding error:', error);
    throw error;
  }
};

// Calculate bounding box based on size configuration
export const calculateBoundingBox = (
  center: { lat: number; lng: number },
  radiusKm: number,
  zoomFactor: number = 1.0
): mapboxgl.LngLatBounds => {
  // Approximate degrees per km
  const degPerKmLat = 1 / 110.574;
  const degPerKmLng = 1 / (111.320 * Math.cos(center.lat * Math.PI / 180));
  
  const adjustedRadius = radiusKm * zoomFactor;
  const deltaLat = adjustedRadius * degPerKmLat;
  const deltaLng = adjustedRadius * degPerKmLng;
  
  return new mapboxgl.LngLatBounds(
    [center.lng - deltaLng, center.lat - deltaLat], // SW corner
    [center.lng + deltaLng, center.lat + deltaLat]  // NE corner
  );
};


// Get Terrain-RGB tile URL for 3D elevation data
export const getTerrainTileUrl = (
  bounds: mapboxgl.LngLatBounds,
  zoom: number = 14,
  tileSize: number = 512
): string => {
  const apiKey = getMapboxApiKey();
  const center = bounds.getCenter();
  
  return `https://api.mapbox.com/v4/mapbox.terrain-rgb/${zoom}/${center.lng}/${center.lat}/${tileSize}x${tileSize}@2x.png?access_token=${apiKey}`;
};

// Get static image URL for 3D relief preview (center + zoom, no bounds)
export const getStaticImageUrl = (
  lat: number,
  lng: number,
  zoom: number,
  style: string,
  width: number,
  height: number,
): string => {
  const apiKey = getMapboxApiKey();
  if (!apiKey) return '';

  // Transform 'mapbox://styles/mapbox/light-v11' → 'mapbox/light-v11'
  const styleId = style.replace(/^mapbox:\/\/styles\//, '') || 'mapbox/light-v11';

  return `https://api.mapbox.com/styles/v1/${styleId}/static/${lng},${lat},${zoom},0/${width}x${height}@2x?access_token=${apiKey}`;
};

// Get static map image URL for texture
export const getStaticMapUrl = (
  bounds: mapboxgl.LngLatBounds,
  width: number = 512,
  height: number = 512,
  style: string = 'mapbox/streets-v11'
): string => {
  const apiKey = getMapboxApiKey();
  const center = bounds.getCenter();
  const padding = 50; // pixels
  
  // Calculate appropriate zoom level based on bounds
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const lngDiff = Math.abs(ne.lng - sw.lng);
  const latDiff = Math.abs(ne.lat - sw.lat);
  
  // Rough zoom calculation (can be refined)
  const zoom = Math.min(
    Math.floor(8 - Math.log2(Math.max(lngDiff, latDiff))),
    16
  );
  
  return `https://api.mapbox.com/styles/v1/${style}/static/` +
    `[${sw.lng},${sw.lat},${ne.lng},${ne.lat}]/` +
    `${width}x${height}@2x?padding=${padding}&access_token=${apiKey}`;
};